import axios from 'axios';
import type { AxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useUpgradeModalStore } from '@/stores/upgradeModalStore';
import { COMPANY_ACCESS_REVOKED, recoverCompanyAccess, refreshSession } from './sessionRecovery';

export type ApiError = AxiosError<{ detail: string }>;

const API_BASE = import.meta.env.VITE_API_URL || '';

const apiClient = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  withCredentials: true, // Send httpOnly cookies
  timeout: 30_000, // 30s — prevent infinite hangs on slow/dead backends
  headers: {
    'Content-Type': 'application/json',
  },
});

// Session refresh and company-access recovery live in ./sessionRecovery (backend H20):
// every refresh in the app goes through refreshSession(), one at a time across tabs.

// Response interceptor: handle 401 (token expired)
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}> = [];

function processQueue(error: unknown) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(undefined);
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Callers can pass `{ _silentOnError: true }` in the axios config to
    // suppress the global toast — e.g. detail pages that render their own
    // "not found" state and don't want a parallel toast.
    const silent = Boolean(originalRequest?._silentOnError);

    // Handle 403 Forbidden — show toast and reject.
    // Exception: module-gate 403s (backend returns "Module 'X' is not enabled
    // for this company" when a tenant hits an endpoint for a disabled module)
    // are swallowed silently. Pages that include widgets calling module-gated
    // endpoints shouldn't spam the user with a toast per failed query; the
    // widget's own empty state handles the UX. Callers that need the data
    // can still .catch() locally.
    if (error.response?.status === 403) {
      const data = error.response?.data as
        | { detail?: string; error_code?: string; resource?: string; current?: number; limit?: number; plan?: string }
        | undefined;
      // Plan-limit hits open the in-app upgrade modal instead of a jarring
      // full-page redirect that discards the user's form data (BUG-060). The
      // backend payload carries enough to tell the user exactly which cap they
      // hit (resource, current, limit, plan name); the modal (mounted at the
      // app root via GlobalUpgradeModal) reads it from the store and stays on
      // the current page. If any field is missing we still open the modal —
      // it degrades to a plain plan picker rather than a dead-end.
      if (data?.error_code === 'PLAN_LIMIT_EXCEEDED') {
        const reason =
          data.resource != null && data.current != null && data.limit != null && data.plan != null
            ? { resource: data.resource, current: data.current, limit: data.limit, plan: data.plan }
            : null;
        useUpgradeModalStore.getState().trigger(reason);
        return Promise.reject(error);
      }
      // Structured 403 details (RBAC hardening): `detail` may be an object
      // ({ code, message, ... }) for 2FA / DoA / SoD denials. Extract a string
      // message so we never render "[object Object]".
      const rawDetail = data?.detail as unknown;
      const detailCode =
        rawDetail && typeof rawDetail === 'object'
          ? (rawDetail as { code?: string }).code
          : undefined;
      const detailMsg =
        typeof rawDetail === 'string'
          ? rawDetail
          : rawDetail && typeof rawDetail === 'object' && typeof (rawDetail as { message?: string }).message === 'string'
            ? (rawDetail as { message: string }).message
            : 'You do not have permission to perform this action.';

      // 2FA-required-for-role → route the user to the enrollment wall (the
      // Security tab) instead of a dead-end. /auth/2fa/* stays reachable, so an
      // unenrolled admin can always enroll and return.
      if (detailCode === '2fa_required_for_role') {
        if (!silent) useNotificationStore.getState().showError(detailMsg);
        if (!window.location.pathname.startsWith('/profile')) {
          sessionStorage.setItem('pending_2fa_enroll', '1');
          window.location.assign('/profile');
        }
        return Promise.reject(error);
      }

      // The user was removed from the token's company (backend H20): one refresh, then a
      // reload into the company the refresh chose. Matched on the stable error_code only;
      // a plain "no access to this company" (an explicit company request) is a normal error.
      // Within the recovery cooldown it falls through to the normal toast.
      if (data?.error_code === COMPANY_ACCESS_REVOKED && recoverCompanyAccess()) {
        return Promise.reject(error);
      }

      // Module-gate denials (backend ModuleGateMiddleware) are swallowed
      // globally so widget-heavy pages don't spam a toast per gated query —
      // the calling surface renders its own inline gate/empty state. Detect
      // via the structured error_code (BUG-056), with the legacy detail-string
      // match kept as a fallback for any pre-structured caller.
      const isModuleGate =
        data?.error_code === 'MODULE_NOT_ENABLED' ||
        /^Module '.+' is not enabled for this company/.test(detailMsg);
      if (!isModuleGate && !silent) {
        useNotificationStore.getState().showError(detailMsg);
      }
      return Promise.reject(error);
    }

    // Show error notification for server errors (500+) and validation errors (422)
    if (error.response?.status >= 500) {
      const detail = error.response?.data?.detail || 'Something went wrong. Please try again.';
      const message = typeof detail === 'string' ? detail : 'Something went wrong. Please try again.';
      if (!silent) useNotificationStore.getState().showError(message);
      return Promise.reject(error);
    }
    if (error.response?.status === 422) {
      const detail = error.response?.data?.detail;
      let message = 'Validation error. Please check your input.';
      if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
        // Structured 422s ({ error, message, fields }) — e.g. BYO credential
        // schema errors. Show the summary; the page maps `fields` inline.
        message = detail.message;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation errors come as an array
        const fields = detail.map((e: any) => {
          const field = e.loc?.slice(-1)[0] || 'field';
          const msg = e.msg || 'invalid';
          return `${field}: ${msg}`;
        });
        message = fields.join(', ');
      }
      // Pages that render field-level errors inline pass _silentOnError.
      if (!silent) useNotificationStore.getState().showError(message);
      return Promise.reject(error);
    }

    // Don't retry refresh endpoint itself, login endpoints, or already-retried requests.
    // Also skip /auth/me: useSessionValidator calls it on cold load to rehydrate the
    // store from the httpOnly cookie. A 401 there means "no session" — the hook's
    // own .catch() handles it. Intercepting it here causes a spurious refresh attempt
    // that hard-redirects unauthenticated users (including /login and /signup visitors)
    // to /login in an infinite reload loop.
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/2fa/verify') ||
      originalRequest.url?.includes('/auth/me')
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue requests while refresh is in progress
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => apiClient(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // The one shared refresh (sessionRecovery): a concurrent 403/1008 recovery or another
      // tab never posts a second refresh with the same, rotating, refresh token.
      await refreshSession();
      processQueue(null);
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
